/**
 * MaestroChat — Main terminal chat container
 * MAESTRO:P0D
 *
 * Layout:
 *  ┌────────────────────────────────────────────┐
 *  │ MAESTRO header bar (terminal prompt style)  │
 *  ├──────────────┬─────────────────────────────┤
 *  │ Sidebar      │ Message thread               │
 *  │ (desktop)    │                              │
 *  │              │                              │
 *  │              ├─────────────────────────────┤
 *  │              │ ChatInput                    │
 *  └──────────────┴─────────────────────────────┘
 *
 * Mobile: sidebar becomes a bottom sheet toggled from a header button.
 * Responsive at all breakpoints with no horizontal overflow.
 * Fully keyboard-navigable.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { History, Terminal } from 'lucide-react';
import useMaestroChat from '../../../hooks/useMaestroChat';
import ConversationSidebar from './ConversationSidebar';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

/**
 * Typing indicator shown while awaiting assistant response.
 */
function TypingIndicator() {
  return (
    <div className="flex justify-start mb-4" aria-live="polite" aria-label="Maestro is thinking">
      <div className="bg-zinc-900 border-l-2 border-orange-500 px-4 py-3 rounded-xl rounded-bl-sm font-mono text-xs text-zinc-500 flex items-center gap-2">
        <span className="flex gap-1" aria-hidden="true">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </span>
        Thinking…
      </div>
    </div>
  );
}

/**
 * Empty state shown before any messages are sent.
 */
function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center select-none" aria-hidden="true">
      <div className="w-14 h-14 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-4">
        <Terminal size={24} className="text-orange-500" />
      </div>
      <h2 className="text-zinc-300 font-mono text-sm font-semibold mb-1">MAESTRO</h2>
      <p className="text-zinc-600 font-mono text-xs max-w-xs leading-relaxed">
        AI-powered comic & collectibles intelligence.
        Ask about your collection, pricing, grades, or anything comics.
      </p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-sm w-full">
        {[
          'What is my most valuable comic?',
          'Show me recent CGC sales for ASM #1',
          'What comics should I get graded?',
          'Summarize my vault by publisher',
        ].map((q, i) => (
          <p key={i} className="text-[10px] text-zinc-700 font-mono border border-zinc-800 rounded px-2 py-1.5 text-left">
            &gt; {q}
          </p>
        ))}
      </div>
    </div>
  );
}

export default function MaestroChat() {
  const {
    activeConversation,
    messages,
    isLoading,
    error,
    sendMessage,
    loadConversation,
    newConversation,
    archiveConversation,
    conversationList,
    listLoading,
    listHasMore,
    loadMoreConversations,
  } = useMaestroChat();

  // Mobile: bottom sheet state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Scroll to bottom on new messages
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Close sidebar on conversation selection (mobile)
  const handleSelectConversation = useCallback((id) => {
    loadConversation(id);
    setIsSidebarOpen(false);
  }, [loadConversation]);

  const handleNew = useCallback(() => {
    newConversation();
    setIsSidebarOpen(false);
  }, [newConversation]);

  const handleSend = useCallback((text) => {
    sendMessage(text);
  }, [sendMessage]);

  const handleFollowup = useCallback((text) => {
    sendMessage(text);
  }, [sendMessage]);

  return (
    <div
      className="flex flex-col h-screen bg-zinc-950 text-zinc-100 overflow-hidden"
      data-testid="maestro-chat"
    >
      {/* ── Header bar ───────────────────────────────────────────────────── */}
      <header className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-zinc-950 border-b border-zinc-800 z-10">
        <div className="flex items-center gap-3">
          {/* Terminal prompt decoration */}
          <span className="hidden sm:flex items-center gap-1.5 font-mono text-xs text-zinc-600" aria-hidden="true">
            <span className="text-orange-500">●</span>
            <span className="text-yellow-500">●</span>
            <span className="text-green-500">●</span>
          </span>
          <h1 className="font-mono text-sm font-semibold tracking-widest text-zinc-200">
            <span className="text-orange-500">M</span>AESTRO
            {activeConversation?.title && (
              <span className="ml-2 text-zinc-600 font-normal truncate max-w-[160px] sm:max-w-xs hidden sm:inline-block">
                / {activeConversation.title}
              </span>
            )}
          </h1>
        </div>

        {/* Mobile: toggle sidebar */}
        <button
          type="button"
          onClick={() => setIsSidebarOpen(v => !v)}
          aria-label="Toggle conversation history"
          aria-expanded={isSidebarOpen}
          className="md:hidden p-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[48px] min-w-[48px] flex items-center justify-center"
        >
          <History size={18} aria-hidden="true" />
        </button>
      </header>

      {/* ── Body: sidebar + chat panel ──────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar (desktop visible, mobile as bottom sheet) */}
        <ConversationSidebar
          conversations={conversationList}
          activeId={activeConversation?.id ?? null}
          onSelect={handleSelectConversation}
          onNew={handleNew}
          onArchive={archiveConversation}
          hasMore={listHasMore}
          listLoading={listLoading}
          onLoadMore={loadMoreConversations}
          isMobileOpen={isSidebarOpen}
          onMobileClose={() => setIsSidebarOpen(false)}
        />

        {/* ── Chat panel ─────────────────────────────────────────────────── */}
        <main
          className="flex-1 flex flex-col min-w-0"
          aria-label="Chat conversation"
        >
          {/* Message thread */}
          <div
            className="flex-1 overflow-y-auto px-3 sm:px-6 pt-4"
            role="log"
            aria-live="polite"
            aria-label="Messages"
          >
            {messages.length === 0 && !isLoading ? (
              <EmptyState />
            ) : (
              <>
                {messages.map((msg, i) => (
                  <ChatMessage
                    key={msg.id}
                    message={msg}
                    onFollowup={handleFollowup}
                    isLatest={i === messages.length - 1}
                    followupsDisabled={isLoading}
                  />
                ))}
                {isLoading && <TypingIndicator />}
              </>
            )}

            {/* Error banner */}
            {error && (
              <div
                role="alert"
                className="mx-auto max-w-md mb-4 px-4 py-3 bg-red-900/30 border border-red-700/50 rounded-lg text-xs text-red-300 font-mono text-center"
              >
                {error}
              </div>
            )}

            {/* Scroll anchor */}
            <div ref={bottomRef} aria-hidden="true" />
          </div>

          {/* Input */}
          <ChatInput onSend={handleSend} disabled={isLoading} />
        </main>
      </div>
    </div>
  );
}
